import React, { useEffect, useState } from "react";
import { View, StyleSheet, ActivityIndicator } from "react-native";
import { useRoute, useNavigation, NavigationProp } from "@react-navigation/native";
import { useEnrollCourseWithTokenMutation, useMeQuery } from "../graphql/generated/graphql";
import { useAuth } from "../hooks/useAuth";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomButton from "../components/common/general/CustomButton/CustomButton";
import { RootStackParamList } from "../navigation/AppNavigator.web";
import { RouteProp } from "@react-navigation/native";
import BlankLayout from "../layouts/BlankLayout";
import { useAppConfig } from "../AppConfigProvider";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";

const EnrollmentView: React.FC = () => {
  const route = useRoute<RouteProp<RootStackParamList, "Enrollment">>();
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const appConfig = useAppConfig();
  const primaryColor = appConfig?.primaryColor || "#0000ff";
  const routeToken = route.params?.invite;

  const [token, setToken] = useState(routeToken || "");

  const { isAuthenticated } = useAuth();
  const { data: meData } = useMeQuery();
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [enrollmentResult, setEnrollmentResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const [enrollCourseWithToken] = useEnrollCourseWithTokenMutation();

  const isCompanionMode = appConfig.isCompanionMode;

  const navigateToHomepage = () => {
    if (isCompanionMode) {
      navigation.navigate("Companion");
    } else {
      navigation.navigate("Home");
    }
  };

  useEffect(() => {
    if (routeToken) {
      setToken(routeToken);
    }
  }, [routeToken]);

  useEffect(() => {
    if (token) {
      if (!isAuthenticated || !meData?.me?.id) {
        return;
      }

      handleEnrollment();
    }
  }, [token, isAuthenticated, meData]);

  const handleEnrollment = async () => {
    if (!token || !meData?.me?.id) return;

    setIsEnrolling(true);
    try {
      const result = await enrollCourseWithToken({
        variables: {
          token,
        },
      });

      if (result.data?.enrollCourseWithToken.success) {
        setEnrollmentResult({
          success: true,
          message: `Successfully enrolled in course ${result.data?.enrollCourseWithToken.data?.courseName}!`,
        });

        setTimeout(() => {
          navigation.navigate("Custom Course Student", {
            course_id: result.data?.enrollCourseWithToken.data?.courseId,
          });
        }, 2000);
      } else {
        setEnrollmentResult({
          success: false,
          message: result.data?.enrollCourseWithToken.message || "Failed to enroll in course",
        });
        setTimeout(() => {
          navigateToHomepage();
        }, 2000);
      }
    } catch (error) {
      console.error("Enrollment error:", error);
      setEnrollmentResult({
        success: false,
        message: "An error occurred while enrolling in the course",
      });
      setTimeout(() => {
        navigateToHomepage();
      }, 2000);
    } finally {
      setIsEnrolling(false);
    }
  };

  if (!isAuthenticated || !meData?.me?.id) {
    return (
      <BlankLayout>
        <View style={styles.container}>
          <ActivityIndicator size="large" color={primaryColor} />
        </View>
      </BlankLayout>
    );
  }

  if (!token && !routeToken) {
    return (
      <BlankLayout>
        <View style={styles.container}>
          <CustomText text="Invalid Enrollment Link" size="l" weight="bold" style={styles.title} />
          <CustomText
            text="Please contact your administrator to get the enrollment link"
            size="m"
            style={styles.subtitle}
          />
          <CustomButton
            text="Go to homepage"
            onPress={navigateToHomepage}
            size="s"
            styleType="primaryTransparentStroke"
          />
        </View>
      </BlankLayout>
    );
  }

  if (isEnrolling) {
    return (
      <BlankLayout>
        <View style={styles.container}>
          <ActivityIndicator size="large" color={primaryColor} />
        </View>
      </BlankLayout>
    );
  }

  if (enrollmentResult) {
    return (
      <BlankLayout>
        <View style={styles.container}>
          <CustomText
            text={enrollmentResult.message}
            useTranslationText={false}
            size="l"
            weight="bold"
            style={[styles.resultText, { color: enrollmentResult.success ? "#4CAF50" : "#F44336" }]}
          />

          {enrollmentResult.success ? (
            <CustomText text="Redirecting to course page..." size="m" style={styles.subtitle} />
          ) : (
            <>
              <CustomSpacing type="vertical" size="m" />
              <CustomButton
                text="Go to homepage"
                onPress={navigateToHomepage}
                size="s"
                styleType="primaryTransparentStroke"
              />
            </>
          )}
        </View>
      </BlankLayout>
    );
  }

  return (
    <BlankLayout>
      <View style={styles.container}>
        <ActivityIndicator size="large" color={primaryColor} />
      </View>
    </BlankLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    backgroundColor: "#fff",
  },
  title: {
    marginBottom: 10,
  },
  subtitle: {
    textAlign: "center",
    color: "#666",
    marginBottom: 20,
  },
  resultText: {
    textAlign: "center",
    marginBottom: 20,
  },
});

export default EnrollmentView;
